import { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiFetch } from "api/Api";
import {
  FaShieldAlt,
  FaCheck,
  FaTimes,
  FaSave,
  FaUserShield,
  FaKey,
  FaCheckSquare,
  FaSquare,
} from "react-icons/fa";
import "assets/theme/admin_list.css";

function RolePermissions() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissionsMap, setRolePermissionsMap] = useState({}); // { [role_id]: [perm_id, ...] }
  
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Roles & Filter to only Super Admin, Leader, Worker (Exclude Admin & User)
      const rolesData = await apiFetch("super_admin/list_roles");
      const loadedRoles = rolesData.roles || [];
      const allowedRoles = ["super_admin", "superadmin", "leader", "worker"];
      const filteredRoles = loadedRoles.filter((role) => {
        const normalized = (role.role_name || "").toLowerCase().replace(/[\s_-]+/g, "");
        return allowedRoles.includes(normalized);
      });
      setRoles(filteredRoles);

      // 2. Fetch Permissions
      const permData = await apiFetch("super_admin/list_permissions");
      const loadedPerms = permData.permissions || [];
      setPermissions(loadedPerms);

      // 3. Fetch Role Permissions Mapping
      const rpData = await apiFetch("super_admin/list_role_permissions");
      const rpList = rpData.role_permissions || [];

      // Group permission_ids by role_id
      const map = {};
      rpList.forEach((item) => {
        const rId = Number(item.role_id);
        const pId = Number(item.permission_id);
        if (!map[rId]) map[rId] = [];
        if (!map[rId].includes(pId)) map[rId].push(pId);
      });

      setRolePermissionsMap(map);

      // Default select first allowed role if available
      if (filteredRoles.length > 0) {
        const firstRoleId = filteredRoles[0].role_id;
        setSelectedRoleId(firstRoleId);
        setSelectedPermissionIds(map[firstRoleId] || []);
      }
    } catch (err) {
      console.error("Failed to load role permissions data:", err);
      setMessage({ text: err.message || "Failed to load data", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (roleId) => {
    setSelectedRoleId(roleId);
    setSelectedPermissionIds(rolePermissionsMap[roleId] || []);
    setMessage({ text: "", type: "" });
  };

  const togglePermission = (permId) => {
    const pId = Number(permId);
    if (selectedPermissionIds.includes(pId)) {
      setSelectedPermissionIds(selectedPermissionIds.filter((id) => id !== pId));
    } else {
      setSelectedPermissionIds([...selectedPermissionIds, pId]);
    }
  };

  const handleSelectAll = () => {
    const allIds = permissions.map((p) => Number(p.permission_id));
    setSelectedPermissionIds(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedPermissionIds([]);
  };

  const handleSaveRolePermissions = async () => {
    if (!selectedRoleId) {
      alert("Please select a role first.");
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      await apiFetch("super_admin/edit_role_permissions", {
        method: "POST",
        body: JSON.stringify({
          role_id: Number(selectedRoleId),
          permission_ids: selectedPermissionIds.map(Number),
        }),
      });

      // Update local state map
      setRolePermissionsMap((prev) => ({
        ...prev,
        [selectedRoleId]: [...selectedPermissionIds],
      }));

      setMessage({ text: "Role permissions saved successfully!", type: "success" });
    } catch (err) {
      console.error(err);
      setMessage({ text: err.message || "Error saving role permissions", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const activeRole = roles.find((r) => Number(r.role_id) === Number(selectedRoleId));
  const activeRoleName = activeRole ? activeRole.role_name.replace(/_/g, " ") : "";

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="container-fluid py-3">
        {/* Header Card */}
        <div className="admin-header-card mb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 position-relative" style={{ zIndex: 1 }}>
            <div className="d-flex align-items-center gap-3">
              <div className="admin-header-icon-box">
                <FaUserShield />
              </div>
              <div>
                <h4 className="text-white fw-bold mb-1" style={{ fontSize: "1.3rem" }}>
                  Role Permissions Mapping / रोल परवानग्या व्यवस्थापन
                </h4>
                <p className="text-white-50 mb-0" style={{ fontSize: "0.88rem" }}>
                  Assign and configure system permission codes for each user role
                </p>
              </div>
            </div>
          </div>
        </div>

        {message.text && (
          <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show mb-4`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted fw-bold">लोड होत आहे...</p>
          </div>
        ) : (
          <div className="row g-4">
            {/* Left Column: Role Selector */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
                <div className="card-header bg-white py-3 border-bottom">
                  <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <FaShieldAlt className="text-warning" /> Select Role / भूमिका निवडा
                  </h6>
                </div>
                <div className="list-group list-group-flush">
                  {roles.map((role) => {
                    const isSelected = Number(role.role_id) === Number(selectedRoleId);
                    const count = (rolePermissionsMap[role.role_id] || []).length;

                    return (
                      <button
                        key={role.role_id}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3 px-4 ${isSelected ? "active bg-warning text-dark border-warning fw-bold" : ""}`}
                        onClick={() => handleRoleChange(role.role_id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <FaUserShield className={isSelected ? "text-dark" : "text-secondary"} />
                          <span>{role.role_name.replace(/_/g, " ")}</span>
                        </div>
                        <span className={`badge rounded-pill ${isSelected ? "bg-dark text-white" : "bg-light text-dark border"}`}>
                          {count} permissions
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Permission Checkboxes */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-3">
                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                      <FaKey className="text-warning" /> Permissions for <span className="text-warning fw-bold">{activeRoleName}</span>
                    </h6>
                    <small className="text-muted">
                      {selectedPermissionIds.length} of {permissions.length} permissions selected
                    </small>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleSelectAll}>
                      <FaCheckSquare className="me-1" /> Select All
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleDeselectAll}>
                      <FaSquare className="me-1" /> Clear All
                    </button>
                  </div>
                </div>

                <div className="card-body p-4">
                  {permissions.length === 0 ? (
                    <p className="text-muted mb-0">No permissions found in the database.</p>
                  ) : (
                    <div className="row g-3">
                      {permissions.map((perm) => {
                        const pId = Number(perm.permission_id);
                        const isChecked = selectedPermissionIds.includes(pId);

                        return (
                          <div key={perm.permission_id} className="col-md-6 col-xl-4">
                            <div
                              className={`p-3 rounded border transition-all ${isChecked ? "border-warning bg-warning bg-opacity-10 shadow-sm" : "border-light bg-light"}`}
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => togglePermission(pId)}
                            >
                              <div className="form-check d-flex align-items-center gap-2 mb-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-0 shadow-none"
                                  id={`perm-${perm.permission_id}`}
                                  checked={isChecked}
                                  onChange={() => togglePermission(pId)}
                                />
                                <label className="form-check-label fw-semibold text-dark text-break" htmlFor={`perm-${perm.permission_id}`} style={{ cursor: "pointer", fontSize: "0.9rem" }}>
                                  {perm.permission_code}
                                </label>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="card-footer bg-white py-3 border-top d-flex align-items-center justify-content-between">
                  <span className="text-muted small">
                    Changes will take effect immediately after saving.
                  </span>
                  <button
                    type="button"
                    className="btn btn-warning text-dark fw-bold px-4 d-flex align-items-center gap-2 shadow-sm"
                    onClick={handleSaveRolePermissions}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? "Saving..." : "Save Role Permissions / जतन करा"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default RolePermissions;
